import React, { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { apiClient } from "../../services/api/apiClient";

export default function DuctTemplateCopy({
  isOpen,
  onClose,
  onSubmit,
  submittalId,
  onOpenSystemModal,
  onOpenSystemModalWithTemplate,
}) {
  const [selectedOption, setSelectedOption] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      // Reset form state when modal opens
      setSelectedOption("");
      setError(null);
      fetchSystemTitles();
    }
  }, [isOpen]);

  const fetchSystemTitles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get("/duct-template/system-titles");
      if (response.success) {
        // Transform the response data into options format with id and name
        const transformedOptions = response.data.map((system) => ({
          value: system.id,
          label: system.name,
          id: system.id,
          name: system.name,
        }));
        // Sort options alphabetically by name
        const sortedOptions = transformedOptions.sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        const blankTemplate = {
          value: "blank",
          label: "Blank Template",
          id: "blank",
          name: "Blank Template",
        };
        setOptions([blankTemplate, ...sortedOptions]);
      } else {
        setError(response.message || "Failed to load system titles");
      }
    } catch (err) {
      console.error("Error fetching system titles:", err);
      setError(err.message || "Failed to load system titles");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Default to "blank" template if no option is selected
    const templateToUse = selectedOption || "blank";

    // If "Blank Template" is selected (or defaulted), handle differently based on whether submittalId exists
    if (templateToUse === "blank") {
      // If submittalId exists, we can immediately open the system modal
      if (submittalId) {
        onClose();
        if (onOpenSystemModal) {
          onOpenSystemModal();
        }
        return;
      } else {
        // If no submittalId, we need to create the schedule first
        // So call onSubmit to let the parent component handle schedule creation
        if (onSubmit) {
          onSubmit(templateToUse);
        }
        // Don't close here - let the parent component handle it
        return;
      }
    }

    // If a template is selected (not blank), fetch and open modal for editing
    try {
      setLoading(true);
      setError(null);

      // Fetch the template system data
      const response = await apiClient.get(
        `/duct-system/by-id/${templateToUse}`
      );

      // ResponseHelper wraps data in response.data.data
      const templateData = response.data?.data || response.data;

      if (response.success && templateData) {
        // Close this modal
        onClose();

        // Open the system modal with template data
        if (onOpenSystemModalWithTemplate) {
          onOpenSystemModalWithTemplate(templateData);
        }
      } else {
        setError("Failed to load template data");
      }
    } catch (err) {
      console.error("Error fetching template data:", err);
      setError(err.message || "Failed to load template data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Add a System to the Duct Schedule</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select a System Template:
              </label>
              {loading ? (
                <div className="text-sm text-gray-500">
                  Loading templates...
                </div>
              ) : error ? (
                <div className="text-sm text-red-500">{error}</div>
              ) : (
                <select
                  value={selectedOption}
                  onChange={(e) => {
                    setSelectedOption(e.target.value);
                    setError(null); // Clear any previous errors
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={loading || options.length === 0}
                >
                  <option value="">Select an option</option>
                  {options.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                onClick={handleSubmit}
              >
                {!selectedOption || selectedOption === "blank" ? "Start" : "Copy"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
